import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './users.css';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  role: 'user',
  status: 'active',
  avatar_url: '',
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchUsers();
  }, );

  useEffect(() => {
    fetchUsers();
  },);

  const fetchUsers = async () => {
    let query = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: true });

    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
    }

    if (roleFilter) {
      query = query.eq('role', roleFilter);
    }

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;

    if (error) {
      alert(error.message);
      return;
    }

    setUsers(data || []);
    fetchUserStats(data || []);
  };

  const fetchUserStats = async (userList) => {
    const stats = {};

    for (const user of userList) {
      const { data: bookings } = await supabase
        .from('bookings')
        .select('total_amount')
        .eq('user_id', user.id);

      stats[user.id] = {
        bookings: bookings?.length || 0,
        spent:
          bookings?.reduce((sum, booking) => {
            return sum + Number(booking.total_amount || 0);
          }, 0) || 0,
      };
    }

    setUserStats(stats);
  };

  const totalRevenue = Object.values(userStats).reduce(
    (sum, item) => sum + Number(item.spent || 0),
    0
  );

  const openAddModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'user',
      status: user.status || 'active',
      avatar_url: user.avatar_url || '',
    });

    setEditingId(user.id);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let result;

    if (editingId) {
      result = await supabase.from('users').update(form).eq('id', editingId);
    } else {
      result = await supabase.from('users').insert([form]);
    }

    if (result.error) {
      alert(result.error.message);
      return;
    }

    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchUsers();
  };

  const deleteUser = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this user?');

    if (!confirmDelete) return;

    const { error } = await supabase.from('users').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchUsers();
  };

  const changeRole = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';

    const { error } = await supabase
      .from('users')
      .update({ role: newRole })
      .eq('id', user.id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchUsers();
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Users" />

      <main className="dashboard-main">
        <Topbar />

        <section className="users-content">
          <div className="users-header">
            <div>
              <h1>Users Management</h1>
              <p>Manage user accounts and permissions</p>
            </div>

            <button className="add-user-btn" onClick={openAddModal}>
              ♚ Add User
            </button>
          </div>

          <div className="users-filter-card">
            <div className="user-search">
              <span>⌕</span>
              <input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="users-grid">
            {users.map((user) => (
              <div className="user-card" key={user.id}>
                <div className="user-main">
                  <div className={`user-avatar ${user.role === 'admin' ? 'admin' : ''}`}>
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} />
                    ) : (
                      '♙'
                    )}
                  </div>

                  <div>
                    <div className="user-name-row">
                      <h3>{user.name}</h3>
                      <span className={user.status || 'active'}>
                        {user.status || 'active'}
                      </span>
                    </div>

                    <p>✉ {user.email}</p>
                    <p>
                      ▣ Joined{' '}
                      {new Date(user.created_at).toISOString().slice(0, 10)}
                    </p>
                  </div>
                </div>

                <div className="user-mini-stats">
                  <div>
                    <span>Bookings</span>
                    <strong>{userStats[user.id]?.bookings || 0}</strong>
                  </div>

                  <div>
                    <span>Total Spent</span>
                    <strong>${userStats[user.id]?.spent || 0}</strong>
                  </div>
                </div>

                <div className="user-card-bottom">
                  <span className={`role-pill ${user.role}`}>{user.role}</span>

                  <div className="user-actions">
                    <button onClick={() => openEditModal(user)}>✎</button>
                    <button onClick={() => changeRole(user)}>♙</button>
                    <button onClick={() => deleteUser(user.id)}>🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="user-stats-grid">
            <div className="user-stat-box">
              <p>Total Users</p>
              <h2>{users.length}</h2>
            </div>

            <div className="user-stat-box">
              <p>Active Users</p>
              <h2 className="green">
                {users.filter((user) => (user.status || 'active') === 'active').length}
              </h2>
            </div>

            <div className="user-stat-box">
              <p>Admins</p>
              <h2 className="pink">
                {users.filter((user) => user.role === 'admin').length}
              </h2>
            </div>

            <div className="user-stat-box">
              <p>Total Revenue</p>
              <h2>${totalRevenue}</h2>
            </div>
          </div>
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>

      {showModal && (
        <div className="user-modal-overlay">
          <div className="user-modal">
            <div className="user-modal-header">
              <h2>{editingId ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form className="user-form" onSubmit={handleSubmit}>
              <input
                name="name"
                placeholder="Full Name"
                value={form.name}
                onChange={handleChange}
                required
              />

              <input
                name="email"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                required
              />

              <input
                name="phone"
                placeholder="Phone"
                value={form.phone}
                onChange={handleChange}
              />

              <input
                name="avatar_url"
                placeholder="Avatar URL optional"
                value={form.avatar_url}
                onChange={handleChange}
              />

              <select name="role" value={form.role} onChange={handleChange}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>

              <select name="status" value={form.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <div className="user-modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit">
                  {editingId ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;